/**
 * Passerelle MoMent → BoxyLab — l'IDENTITOVIGILANCE (21/10/2026).
 *
 * Avant tout dépôt, l'identité du dossier MoMent est comparée à celle de la
 * demande BoxyLab, ligne à ligne. Le moindre écart BLOQUE, sans bouton « forcer ».
 * La comparaison ignore seulement ce qui n'est pas l'identité : accents, casse,
 * espaces multiples, tirets et apostrophes (« Ben-Salah » = « BEN SALAH »).
 *
 * Module PUR, embarqué par l'extension « Nafis Bridge » ET montré par l'écran
 * MoMent : les deux font le même calcul (testé : ../backend/tests/front-boxylab-identite.test.js).
 */

/** « Aïcha  Ben-Salah » → « AICHA BEN SALAH ». */
export function normaliser(s: string | null | undefined): string {
  return String(s ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[-'’‘`´.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export type Identite = { nom: string; prenom: string; ddn: string };

export type LigneControle = {
  ligne: 'code' | 'nom' | 'prenom' | 'ddn' | 'validation' | 'doublon';
  libelle: string;
  moment: string;
  boxylab: string;
  ok: boolean;
  /** Une ligne d'information (le doublon) ne bloque pas : elle demande une confirmation. */
  bloquante: boolean;
};

export type DemandeLue = {
  demandeId: number;
  patientId: number;
  code: string;
  identite: Identite;
  valideBiologiquement: boolean;
};

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** « 1990-01-02 » → « 02/01/1990 » pour l'écran ; une valeur hors format est montrée telle quelle. */
export function dateFr(iso: string): string {
  const m = DATE.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/**
 * Les lignes de l'écran de confirmation, côte à côte MoMent | BoxyLab.
 * `doublon` : un CR de ce dossier est déjà dans la demande, sous un autre contenu (même nom) — confirmable.
 * `dejaDepose` : le n° du document BoxyLab qui porte DÉJÀ ce CR (même empreinte) — BLOQUANT (Ines, 21/10/2026).
 */
export function comparer(moment: { code: string; identite: Identite }, boxy: DemandeLue, doublon: boolean, dejaDepose: number | null = null): LigneControle[] {
  const memeTexte = (a: string, b: string) => normaliser(a) !== '' && normaliser(a) === normaliser(b);
  return [
    { ligne: 'code', libelle: 'Code dossier', moment: moment.code, boxylab: boxy.code, ok: moment.code.trim() === boxy.code.trim(), bloquante: true },
    { ligne: 'nom', libelle: 'Nom', moment: moment.identite.nom, boxylab: boxy.identite.nom, ok: memeTexte(moment.identite.nom, boxy.identite.nom), bloquante: true },
    { ligne: 'prenom', libelle: 'Prénom', moment: moment.identite.prenom, boxylab: boxy.identite.prenom, ok: memeTexte(moment.identite.prenom, boxy.identite.prenom), bloquante: true },
    { ligne: 'ddn', libelle: 'Date de naissance', moment: dateFr(moment.identite.ddn), boxylab: dateFr(boxy.identite.ddn), ok: DATE.test(moment.identite.ddn) && moment.identite.ddn === boxy.identite.ddn, bloquante: true },
    { ligne: 'validation', libelle: 'Demande BoxyLab', moment: 'CR validé', boxylab: boxy.valideBiologiquement ? 'déjà validée biologiquement' : 'non validée', ok: !boxy.valideBiologiquement, bloquante: true },
    dejaDepose !== null
      ? { ligne: 'doublon', libelle: 'CR déjà déposé', moment: '—', boxylab: `oui, ce même CR (même empreinte) : document n° ${dejaDepose}`, ok: false, bloquante: true }
      : { ligne: 'doublon', libelle: 'CR déjà déposé', moment: '—', boxylab: doublon ? 'oui : un autre PDF de ce dossier est déjà dans la demande' : 'non', ok: !doublon, bloquante: false },
  ];
}

/** Rien ne part si une ligne bloquante est rouge. */
export const toutConforme = (lignes: LigneControle[]): boolean => lignes.every((l) => l.ok || !l.bloquante);

/** Le message de blocage, tel que le cahier des charges le fixe pour la demande validée. */
export function motifBlocage(lignes: LigneControle[]): string | null {
  const rouges = lignes.filter((l) => !l.ok && l.bloquante);
  if (!rouges.length) return null;
  if (rouges.some((l) => l.ligne === 'validation')) return 'La demande est déjà validée dans BoxyLab : déposez le CR avant la validation.';
  const deja = rouges.find((l) => l.ligne === 'doublon');
  if (deja && rouges.length === 1) return `Ce CR est déjà dans la demande BoxyLab (${deja.boxylab.replace(/^.*: /, '')}) : rien à envoyer.`;
  return `Identité discordante (${rouges.map((l) => l.libelle.toLowerCase()).join(', ')}) : envoi bloqué. Vérifiez le dossier et la demande.`;
}
